// Google Drive from the browser, for Vault: folders and resumable uploads that
// go straight from the app to Drive (Supabase is never in the path). Access
// tokens come from the `google` Edge Function. fetch is injectable for tests.

const DRIVE = 'https://www.googleapis.com/drive/v3';
const UPLOAD = 'https://www.googleapis.com/upload/drive/v3/files';
const FOLDER_MIME = 'application/vnd.google-apps.folder';
const FILE_FIELDS = 'id,name,mimeType,webViewLink,size';
/** So Hive Uploads can live in a shared drive as well as someone's My Drive. */
const ALL_DRIVES = 'supportsAllDrives=true';

/** Upload chunk size: a multiple of 256 KiB, as Drive requires. */
export const CHUNK_BYTES = 8 * 1024 * 1024;

export interface DriveFile {
  id: string;
  name: string;
  mimeType: string;
  webViewLink?: string;
}

export class DriveError extends Error {
  constructor(
    message: string,
    readonly status: number,
  ) {
    super(message);
  }
}

export interface DriveClient {
  token: () => Promise<string>;
  fetch?: typeof fetch;
}

async function call(client: DriveClient, url: string, init: RequestInit = {}): Promise<Response> {
  const doFetch = client.fetch ?? fetch;
  const headers = new Headers(init.headers);
  headers.set('Authorization', `Bearer ${await client.token()}`);
  const res = await doFetch(url, { ...init, headers });
  if (!res.ok && res.status !== 308) {
    const text = await res.text().catch(() => '');
    let message = text;
    try {
      message = (JSON.parse(text) as { error?: { message?: string } }).error?.message ?? text;
    } catch {
      // Not JSON; keep the text.
    }
    throw new DriveError(`Google Drive: ${message || res.statusText}`, res.status);
  }
  return res;
}

const quote = (s: string) => s.replace(/\\/g, '\\\\').replace(/'/g, "\\'");

/** A file's name and type, if Hive can see it (it created it, or a member picked it). */
export async function getFile(client: DriveClient, fileId: string): Promise<DriveFile & { thumbnailLink?: string }> {
  const res = await call(
    client,
    `${DRIVE}/files/${encodeURIComponent(fileId)}?fields=${FILE_FIELDS},thumbnailLink&${ALL_DRIVES}`,
  );
  return (await res.json()) as DriveFile & { thumbnailLink?: string };
}

/** A folder under `parentId` with this name, if Hive can see one (drive.file sees only its own and picked files). */
export async function findFolder(client: DriveClient, name: string, parentId: string): Promise<DriveFile | null> {
  const q = `name = '${quote(name)}' and '${quote(parentId)}' in parents and mimeType = '${FOLDER_MIME}' and trashed = false`;
  const res = await call(
    client,
    `${DRIVE}/files?${new URLSearchParams({
      q,
      fields: `files(${FILE_FIELDS})`,
      pageSize: '1',
      corpora: 'allDrives',
      includeItemsFromAllDrives: 'true',
      supportsAllDrives: 'true',
    })}`,
  );
  const data = (await res.json()) as { files?: DriveFile[] };
  return data.files?.[0] ?? null;
}

export async function createFolder(client: DriveClient, name: string, parentId?: string): Promise<DriveFile> {
  const res = await call(client, `${DRIVE}/files?fields=${FILE_FIELDS}&${ALL_DRIVES}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ name, mimeType: FOLDER_MIME, ...(parentId ? { parents: [parentId] } : {}) }),
  });
  return (await res.json()) as DriveFile;
}

/** Files (not folders) Hive can see that match a Drive query, with their size. */
async function listFiles(client: DriveClient, q: string, pageSize = 20): Promise<(DriveFile & { size?: string })[]> {
  const res = await call(
    client,
    `${DRIVE}/files?${new URLSearchParams({
      q: `${q} and mimeType != '${FOLDER_MIME}' and trashed = false`,
      fields: `files(${FILE_FIELDS})`,
      pageSize: String(pageSize),
      corpora: 'allDrives',
      includeItemsFromAllDrives: 'true',
      supportsAllDrives: 'true',
    })}`,
  );
  return ((await res.json()) as { files?: (DriveFile & { size?: string })[] }).files ?? [];
}

/**
 * Files whose name or contents match `text`, by Drive's own full-text search.
 * With drive.file that's only files Hive created or a member picked: Vault's.
 */
export async function searchDrive(client: DriveClient, text: string, limit = 10): Promise<DriveFile[]> {
  const words = text.trim();
  if (!words) return [];
  return listFiles(client, `fullText contains '${quote(words)}'`, limit);
}

/**
 * A copy of a file Hive already put in Drive: one tagged with this key and value
 * (appProperties), else one in `folderId` with one of `names` and the same size. Files in
 * `exclude` are skipped, so two different files never land on the same copy.
 */
export async function findExistingCopy(
  client: DriveClient,
  match: { property: [string, string]; folderId: string; names: readonly string[]; size: number },
  exclude: ReadonlySet<string> = new Set(),
): Promise<DriveFile | null> {
  const [key, value] = match.property;
  const tagged = await listFiles(client, `appProperties has { key='${quote(key)}' and value='${quote(value)}' }`);
  const hit = tagged.find((f) => !exclude.has(f.id));
  if (hit) return hit;
  const names = match.names.map((n) => `name = '${quote(n)}'`).join(' or ');
  const named = await listFiles(client, `(${names}) and '${quote(match.folderId)}' in parents`);
  return named.find((f) => !exclude.has(f.id) && Number(f.size) === match.size) ?? null;
}

/** Walks (and creates as needed) folders under `rootId`: e.g. ["Moon Rush", "Grid"]. Returns the last folder's id. */
export async function ensureFolderPath(client: DriveClient, rootId: string, names: readonly string[]): Promise<string> {
  let parent = rootId;
  for (const name of names) {
    parent = (await findFolder(client, name, parent))?.id ?? (await createFolder(client, name, parent)).id;
  }
  return parent;
}

/** The byte ranges a resumable upload sends, in order. */
export function chunkRanges(size: number, chunk = CHUNK_BYTES): { start: number; end: number }[] {
  if (size === 0) return [{ start: 0, end: 0 }];
  const out: { start: number; end: number }[] = [];
  for (let start = 0; start < size; start += chunk) out.push({ start, end: Math.min(start + chunk, size) });
  return out;
}

/** How many bytes Drive says it has, from a 308's Range header ("bytes=0-1048575"). */
export function receivedBytes(range: string | null): number {
  const m = range ? /bytes=0-(\d+)/.exec(range) : null;
  return m ? Number(m[1]) + 1 : 0;
}

/**
 * Uploads a file into `folderId` with Drive's resumable protocol: one request
 * opens a session, then the file goes up in chunks, resuming from whatever
 * Drive says it already has. `onProgress` gets the fraction sent.
 */
export async function uploadFile(
  client: DriveClient,
  file: Blob & { name: string },
  folderId: string,
  onProgress: (fraction: number) => void = () => {},
  chunk = CHUNK_BYTES,
  /** Hidden key/values only Hive sees on the file (Drive appProperties), e.g. the Slack file it came from. */
  appProperties?: Record<string, string>,
): Promise<DriveFile> {
  const type = file.type || 'application/octet-stream';
  const start = await call(client, `${UPLOAD}?uploadType=resumable&fields=${FILE_FIELDS}&${ALL_DRIVES}`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json; charset=UTF-8',
      'X-Upload-Content-Type': type,
      'X-Upload-Content-Length': String(file.size),
    },
    body: JSON.stringify({ name: file.name, parents: [folderId], ...(appProperties ? { appProperties } : {}) }),
  });
  const session = start.headers.get('Location');
  if (!session) throw new DriveError('Google Drive didn’t open an upload session.', 0);

  let offset = 0;
  for (let attempt = 0; offset < file.size || file.size === 0; attempt++) {
    if (attempt > chunkRanges(file.size, chunk).length + 5) throw new DriveError('The upload kept stalling.', 0);
    const end = Math.min(offset + chunk, file.size);
    const res = await call(client, session, {
      method: 'PUT',
      headers: {
        'Content-Range': file.size === 0 ? 'bytes */0' : `bytes ${offset}-${end - 1}/${file.size}`,
      },
      body: file.slice(offset, end),
    });
    if (res.status === 308) {
      offset = receivedBytes(res.headers.get('Range'));
      onProgress(file.size ? offset / file.size : 1);
      continue;
    }
    onProgress(1);
    return (await res.json()) as DriveFile;
  }
  throw new DriveError('The upload ended without a file.', 0);
}

/** A thumbnail URL that works for anyone signed in to Google who can see the file. */
export function driveThumbnail(fileId: string, width = 640): string {
  return `https://drive.google.com/thumbnail?id=${encodeURIComponent(fileId)}&sz=w${width}`;
}

/** Drive's own player/preview, for an iframe. */
export function drivePreviewUrl(fileId: string): string {
  return `https://drive.google.com/file/d/${encodeURIComponent(fileId)}/preview`;
}
